import { AutocompleteRenderGroupParams } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useField } from 'formik'

import Autocomplete from './Autocomplete'

export interface SearchFieldBaseOption {
  value: number
  inputLabel: string
  optionLabel: string
}

export interface SearchFieldBaseProps {
  name: string
  label: string
  fetchFilteredValues: (filterString: string, maxDisplayedResultCount?: number) => Promise<SearchFieldBaseOption[]>
  onValueChange: (value: SearchFieldBaseOption | null) => void
  isOptionEqualToValue: (option: SearchFieldBaseOption, value: SearchFieldBaseOption) => boolean
  initialSearchValue?: string
  maxDisplayedResultCount?: number
  allowValueCreation?: boolean /* determines if a new value can be chosen/added that is not part of the options */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: SearchFieldBaseOption) => React.ReactNode
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode
  groupBy?: (option: SearchFieldBaseOption) => string
  disabled?: boolean
}

export type BasicProps = Omit<SearchFieldBaseProps, 'fetchFilteredValues'>

/**
 * Common input field that asynchroniously fetches entries filtered by the input
 */
const SearchFieldBase = ({
  name,
  label,
  fetchFilteredValues,
  initialSearchValue,
  maxDisplayedResultCount,
  allowValueCreation,
  onValueChange,
  renderGroup,
  renderOption,
  isOptionEqualToValue,
  groupBy,
  disabled,
}: SearchFieldBaseProps): React.ReactElement => {
  const [options, setOptions] = useState<SearchFieldBaseOption[]>([])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [field, meta] = useField(name)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [fieldValue, setFieldValue] = useState<SearchFieldBaseOption | null>(null)

  const loadCompanyOptions = async (value: string): Promise<void> => {
    if (!value || value.length < 3 || meta.initialValue === value) {
      setIsMenuOpen(false)
      return
    }

    setLoading(true)

    try {
      const filteredValues = await fetchFilteredValues(value, maxDisplayedResultCount)
      setOptions(filteredValues)
      setLoading(false)
    } catch (error) {
      setIsMenuOpen(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialSearchValue && !searchValue) {
      setSearchValue(initialSearchValue)
      loadCompanyOptions(initialSearchValue)
    }
  }, [initialSearchValue]) //eslint-disable-line

  useEffect(() => {
    if (initialSearchValue === searchValue) {
      setFieldValue(options.find(option => option.value === field.value) ?? null)
      setIsMenuOpen(false)
    }

    if (searchValue && searchValue !== initialSearchValue && options.length > 0) {
      setIsMenuOpen(true)
    } else {
      setIsMenuOpen(false)
    }
  }, [options]) //eslint-disable-line

  const handleInputChange = (event: React.SyntheticEvent | null, value: string): void => {
    if (event?.type === 'click' && value !== '') return
    setSearchValue(value)
    loadCompanyOptions(value)
  }

  const handleChange = (value: SearchFieldBaseOption | null): void => {
    onValueChange(value)
    setFieldValue(value)
    setIsMenuOpen(false)
  }

  return (
    <Autocomplete<SearchFieldBaseOption>
      name={name}
      handleInputChange={handleInputChange}
      handleChange={handleChange}
      value={allowValueCreation ? field.value : fieldValue}
      isMenuOpen={isMenuOpen}
      options={options}
      loading={loading}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={option => (typeof option === 'string' ? option : option.optionLabel)}
      label={label}
      disabled={disabled}
    />
  )
}

export default SearchFieldBase
